import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ActivityProgressBadge } from './ActivityProgressBadge';
import { OperativeActivity, getProgress, getStepTitlesForActivity } from '../../services/operativeActivities';
import { CheckCircle2, Circle, Lock, Calendar, User, FileText, Briefcase, Edit, ChevronRight, ChevronDown, Tag, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ActivityDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: OperativeActivity | null;
  onStepComplete: (activityId: string, stepIndex: number) => void;
  onEdit?: (activity: OperativeActivity) => void;
}
export function ActivityDetailModal({
  isOpen,
  onClose,
  activity,
  onStepComplete,
  onEdit
}: ActivityDetailModalProps) {
  const [justCompleted, setJustCompleted] = useState<number | null>(null);
  const [allDone, setAllDone] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  if (!activity) return null;
  const progress = getProgress(activity);
  const titles = getStepTitlesForActivity(activity);
  const handleCompleteStep = (stepIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onStepComplete(activity.id, stepIndex);
    setJustCompleted(stepIndex);
    setTimeout(() => setJustCompleted(null), 1200);
    const remainingUncompleted = activity.stepsCompleted.filter((s, i) => !s && i !== stepIndex).length;
    if (remainingUncompleted === 0) {
      setTimeout(() => setAllDone(true), 600);
      setTimeout(() => {
        setAllDone(false);
        onClose();
      }, 2500);
    }
  };
  const toggleStep = (index: number) => {
    setExpandedSteps(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Attività operativa — ${activity.title}`} size="2xl" data-id="element-335" data-mp="1ncfjpo1njakb">
      <AnimatePresence mode="wait" data-id="element-336" data-mp="1dcoofk59orj6">
        {allDone ? <motion.div key="alldone" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center py-16" data-id="element-337" data-mp="1de9jrbzeoei3">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-338" data-mp="217rd9s42muzn">
              <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-339" data-mp="319xxcn8piduy" />
            </motion.div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-340" data-mp="31br19urxqjz9">
              Attività completata!
            </p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-341" data-mp="3b7u6xalzx0qy">
              Tutti gli step sono stati completati con successo.
            </p>
          </motion.div> : <motion.div key="content" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-342" data-mp="3g5du500o8nv1">
            <div className="space-y-6" data-id="element-343" data-mp="3p0a9kaaziwrc">
              {/* Info Header */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-344" data-mp="3txa7fxxokt7d">
                <div data-id="element-345" data-mp="2u50xwk2jmj5">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-346" data-mp="7ren93c5vi2r">
                    <Tag className="h-3 w-3" data-id="element-347" data-mp="14iifhipn47vy" /> Tipo
                  </span>
                  <div className="mt-1" data-id="element-348" data-mp="14gp5pujqd7jq">
                    <Badge variant={activity.activityType === 'standard' ? 'info' : 'warning'} size="sm" data-id="element-349" data-mp="zkgx7001gclj">
                      {activity.activityType === 'standard' ? 'Standard' : 'Personalizzata'}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-350" data-mp="ukcgp9z4rhvb">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-351" data-mp="ukuh8istsi91">
                    <Briefcase className="h-3 w-3" data-id="element-352" data-mp="qhxzeub40q65" /> Cliente
                  </span>
                  <p className="font-medium text-sm text-slate-900 mt-0.5" data-id="element-353" data-mp="25yzp3mw5tc9n">
                    {activity.clientName}
                  </p>
                </div>
                <div data-id="element-354" data-mp="220ieuuhfzlo0">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-355" data-mp="29h5kz0a3br0n">
                    <FileText className="h-3 w-3" data-id="element-356" data-mp="2eh1pr1qm3mh9" /> Contratto
                  </span>
                  <p className="font-mono font-medium text-sm text-slate-900 mt-0.5" data-id="element-357" data-mp="1zjr798fjxgc1">
                    {activity.contractNumber}
                  </p>
                </div>
                <div data-id="element-358" data-mp="24kf44aqgore6">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-359" data-mp="2s8g5mzp837k3">
                    <ShoppingBag className="h-3 w-3" data-id="element-360" data-mp="2x7sxjyuyb4tj" /> Prodotto
                  </span>
                  <p className="font-medium text-sm text-slate-900 mt-0.5" data-id="element-361" data-mp="2iimuu0wwmwgw">
                    {activity.product || '—'}
                  </p>
                </div>
                <div data-id="element-362" data-mp="2nfdcuts9s24y">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-363" data-mp="3b70xwv2uoizb">
                    <User className="h-3 w-3" data-id="element-364" data-mp="3fxhciiieqan9" /> Operatore
                  </span>
                  <p className="font-medium text-sm text-slate-900 mt-0.5" data-id="element-365" data-mp="3ptr86lyjxlx1">
                    {activity.operator}
                  </p>
                </div>
                <div data-id="element-366" data-mp="3kv66sj7kcw70">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-367" data-mp="3ksjyghcumcnq">
                    <Calendar className="h-3 w-3" data-id="element-368" data-mp="3guw3jeb5ojvu" /> Scadenza
                  </span>
                  <p className={`font-medium text-sm mt-0.5 ${progress.isOverdue ? 'text-red-600 font-bold' : 'text-slate-900'}`} data-id="element-369" data-mp="3bw2mxd0kocrg">
                    {formatDate(activity.deadline)}
                    {progress.isOverdue && <span className="ml-1 text-xs text-red-500" data-id="element-370" data-mp="2x5velq4a4a4h">
                        (scaduta)
                      </span>}
                  </p>
                </div>
              </div>

              {/* Overall Progress */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100" data-id="element-371" data-mp="2j5at2m6zc07a">
                <span className="text-sm font-semibold text-seform-navy" data-id="element-372" data-mp="ynrwjex3sxjq">
                  Avanzamento complessivo
                </span>
                <div className="w-48" data-id="element-373" data-mp="13ks2zqxv82bd">
                  <ActivityProgressBadge currentStep={progress.currentStep} totalSteps={progress.totalSteps} percentage={progress.percentage} isCompleted={progress.isCompleted} isOverdue={progress.isOverdue} size="md" data-id="element-374" data-mp="18lg4x7nmu3kd" />
                </div>
              </div>

              {/* Steps Timeline */}
              <div className="space-y-0" data-id="element-375" data-mp="ox746980wp99">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4" data-id="element-376" data-mp="ovcdsfdzg4xs">
                  Step dell'attività
                </h4>
                <div className="relative" data-id="element-377" data-mp="3tcgi34z0i4ws">
                  {titles.map((title, index) => {
                const isStepCompleted = activity.stepsCompleted[index];
                const isCurrentStep = !isStepCompleted && (index === 0 || activity.stepsCompleted[index - 1]);
                const isLocked = !isStepCompleted && index > 0 && !activity.stepsCompleted[index - 1];
                const wasJustCompleted = justCompleted === index;
                const isExpanded = expandedSteps[index];
                return <div key={index} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-378" data-mp="34ppdnp76vl9r" data-mpi={index}>
                        {/* Connecting line */}
                        {index < titles.length - 1 && <div className={`absolute left-[15px] top-8 bottom-6 w-0.5 ${isStepCompleted ? 'bg-green-300' : 'bg-slate-200'}`} data-id="element-379" data-mp="3eg0ta908jiwh" />}

                        {/* Step icon */}
                        <div className="relative z-10 flex-shrink-0" data-id="element-380" data-mp="awcry7l54nyz">
                          {isStepCompleted || wasJustCompleted ? <motion.div initial={wasJustCompleted ? {
                      scale: 0
                    } : {
                      scale: 1
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20
                    }} className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-381" data-mp="fuwfudjao9tz">
                              <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-382" data-mp="2drigfx44w0fu" />
                            </motion.div> : isCurrentStep ? <div className="w-8 h-8 rounded-full bg-seform-orange/10 border-2 border-seform-orange flex items-center justify-center" data-id="element-383" data-mp="23unf76gh7lxv">
                              <span className="text-xs font-bold text-seform-orange" data-id="element-384" data-mp="28tgth13l7gz3">
                                {index + 1}
                              </span>
                            </div> : <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-385" data-mp="2mt9i8qvjzn4f">
                              {isLocked ? <Lock className="h-3.5 w-3.5 text-slate-300" data-id="element-386" data-mp="2rpq7joeho7j3" /> : <Circle className="h-3.5 w-3.5 text-slate-300" data-id="element-387" data-mp="3k83weyhukj61" />}
                            </div>}
                        </div>

                        {/* Step content */}
                        <div className={`flex-1 rounded-lg border transition-all overflow-hidden ${isStepCompleted ? 'bg-green-50/50 border-green-100' : isCurrentStep ? 'bg-orange-50/50 border-seform-orange/20 shadow-sm' : 'bg-slate-50/50 border-slate-100'}`} data-id="element-388" data-mp="13tsfnmti9pgd">
                          <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-black/5 transition-colors" onClick={() => toggleStep(index)} data-id="element-389" data-mp="puspgaqch2i3">
                            <div className="flex items-center gap-3" data-id="element-390" data-mp="tvmdr57p8jbi">
                              <div className={`p-1 rounded-md ${isExpanded ? 'bg-slate-200/50' : ''}`} data-id="element-391" data-mp="tygvuq38ps1r">
                                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" data-id="element-392" data-mp="12y506z1awda0" /> : <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-393" data-mp="17vwtnxhlg7gb" />}
                              </div>
                              <div data-id="element-394" data-mp="1bwqj4vaopubb">
                                <span className={`text-xs font-semibold uppercase tracking-wider ${isStepCompleted ? 'text-green-600' : isCurrentStep ? 'text-seform-orange' : 'text-slate-400'}`} data-id="element-395" data-mp="1gxwb2qwitdjs">
                                  Step {index + 1}
                                </span>
                                <h5 className={`font-semibold text-sm ${isStepCompleted ? 'text-green-800' : isCurrentStep ? 'text-seform-navy' : 'text-slate-400'}`} data-id="element-396" data-mp="t5z0t48pytks">
                                  {title}
                                </h5>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-397" data-mp="3u3udnv7jvo6j">
                              {isCurrentStep && <Button size="sm" onClick={e => handleCompleteStep(index, e)} endContent={<ChevronRight className="h-3.5 w-3.5" data-id="element-399" data-mp="3b5i0lydipu5o" />} data-id="element-398" data-mp="3p3gdt0g3rkjl">
                                  Completa step
                                </Button>}
                              {isStepCompleted && <Badge variant="success" size="sm" data-id="element-400" data-mp="2wh3yxqru8bqp">
                                  Completato
                                </Badge>}
                              {isLocked && <Badge variant="neutral" size="sm" data-id="element-401" data-mp="2nh8ib6fijoja">
                                  Bloccato
                                </Badge>}
                            </div>
                          </div>

                          {/* Expandable Details */}
                          <AnimatePresence data-id="element-402" data-mp="hm6dcddxa47d">
                            {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden" data-id="element-403" data-mp="mkri8q44dc25">
                                <div className="p-4 pt-0 border-t border-slate-100/50 mt-2" data-id="element-404" data-mp="1xyv9hojbom3i">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-id="element-405" data-mp="282s68xvf18rp">
                                    <div className="bg-white p-3 rounded-lg border border-slate-100" data-id="element-406" data-mp="2348hyqtcnkw6">
                                      <h6 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5" data-id="element-407" data-mp="2gzays8zqiunr">
                                        <User className="h-3.5 w-3.5 text-seform-orange" data-id="element-408" data-mp="2qvdwn2nk074w" />
                                        Cosa fa il consulente
                                      </h6>
                                      <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs" data-id="element-409" data-mp="2zwc6w2ol5avx">
                                        <li data-id="element-410" data-mp="2vu7ch1qbca88">
                                          Analisi preliminare della
                                          documentazione
                                        </li>
                                        <li data-id="element-411" data-mp="11hhkipji9b3h">Raccolta dati e informazioni</li>
                                        <li data-id="element-412" data-mp="30lpphbs7bnhh">Stesura del documento finale</li>
                                        <li data-id="element-413" data-mp="30nu16fwcdqsb">Verifica conformità normativa</li>
                                      </ul>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border border-slate-100" data-id="element-414" data-mp="3aiuedjdflx1z">
                                      <h6 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5" data-id="element-415" data-mp="3fji503xr0bvo">
                                        <Briefcase className="h-3.5 w-3.5 text-blue-500" data-id="element-416" data-mp="3jk1zezqxt1e4" />
                                        Cosa fa il cliente
                                      </h6>
                                      <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs" data-id="element-417" data-mp="3tan331lgww3w">
                                        <li data-id="element-418" data-mp="25wu8ofav279">Fornisce planimetrie aggiornate</li>
                                        <li data-id="element-419" data-mp="74hw0sa79txt">
                                          Compila il questionario informativo
                                        </li>
                                        <li data-id="element-420" data-mp="1vmt3qycidbka">Garantisce l'accesso ai locali</li>
                                        <li data-id="element-421" data-mp="1rl7j2qtqfszj">Firma la documentazione</li>
                                      </ul>
                                    </div>
                                  </div>
                                  <div className="mt-3 flex gap-4 text-xs font-medium text-slate-500 bg-slate-100/50 p-2 rounded-md" data-id="element-422" data-mp="1hmrsqq2j4nr3">
                                    <div className="flex items-center gap-1" data-id="element-423" data-mp="2f9idbb4xaueu">
                                      <Calendar className="h-3.5 w-3.5" data-id="element-424" data-mp="2aaxcpalre8pv" />
                                      <span data-id="element-425" data-mp="2adtcz5l2x9hh">Inizio previsto: 10/05/2025</span>
                                    </div>
                                    <div className="flex items-center gap-1" data-id="element-426" data-mp="20lx9ff084r5v">
                                      <Calendar className="h-3.5 w-3.5" data-id="element-427" data-mp="20jt7v3pss48r" />
                                      <span data-id="element-428" data-mp="2y2cjbfkkjc6x">Fine prevista: 20/05/2025</span>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>;
              })}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-429" data-mp="1wooug5hyp5i0">
                <Button variant="flat" color="default" onClick={onClose} data-id="element-430" data-mp="2kczput4w2cyw">
                  Chiudi
                </Button>
                <Button variant="flat" startContent={<Edit className="h-4 w-4" data-id="element-432" data-mp="2bdcno54c534s" />} onClick={() => {
              if (onEdit) onEdit(activity);
            }} data-id="element-431" data-mp="2pb1676axjy6o">
                  Modifica
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Modal>;
}